import type React from "react";
import { CheckCircle2, Circle, AlertTriangle, ListChecks } from "lucide-react";
import { theme, type as text } from "../theme";

// ── What's missing ────────────────────────────────────────────────────────────
//
// A short checklist on a record's detail view: what has been filled in, what
// is still open and what is overdue. Pure presentation — each page works out
// its own items from the record's fields. Hidden once everything is done.

export interface NextStepItem {
  done: boolean;
  label: string;
  hint?: string;
  urgent?: boolean;
}

export function NextSteps({ items, title = "What's missing", action }: { items: NextStepItem[]; title?: string; /** Optional button, e.g. "Fill these in", under the list. */ action?: React.ReactNode }) {
  const open = items.filter((i) => !i.done);
  if (items.length === 0 || open.length === 0) return null;
  const urgent = open.some((i) => i.urgent);
  // Open items first, the urgent ones at the top; done items last, for context.
  const ordered = [...items].sort((a, b) => Number(a.done) - Number(b.done) || Number(!!b.urgent) - Number(!!a.urgent));

  return (
    <div style={{
      border: `1px solid ${urgent ? theme.color.danger : theme.color.warning}`,
      background: urgent ? theme.color.dangerBg : theme.color.warningBg,
      borderRadius: 3, padding: "10px 14px", marginBottom: 12,
    }}>
      <div style={{ ...text.sectionTitle, display: "flex", alignItems: "center", gap: 6, color: urgent ? theme.color.dangerText : theme.color.warningText, margin: "0 0 6px" }}>
        <ListChecks size={14} />
        {title}
        <span style={{ ...text.meta, color: "inherit", fontWeight: 600, marginLeft: 4 }}>
          {items.length - open.length} of {items.length} done
        </span>
      </div>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
        {ordered.map((i, n) => {
          const color = i.done ? theme.color.successText : i.urgent ? theme.color.dangerText : theme.color.warningText;
          const Icon = i.done ? CheckCircle2 : i.urgent ? AlertTriangle : Circle;
          return (
            <li key={`${i.label}-${n}`} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: text.hint.fontSize, lineHeight: text.hint.lineHeight }}>
              <Icon size={14} style={{ color, flexShrink: 0, marginTop: 1 }} />
              <span style={{ minWidth: 0 }}>
                <span style={{ color: i.done ? theme.color.muted : theme.color.text, fontWeight: i.done ? 400 : 600, textDecoration: i.done ? "line-through" : "none" }}>{i.label}</span>
                {i.hint && <span style={{ ...text.meta, display: "block", color: i.urgent && !i.done ? theme.color.dangerText : theme.color.muted }}>{i.hint}</span>}
              </span>
            </li>
          );
        })}
      </ul>
      {action && <div style={{ marginTop: 8 }}>{action}</div>}
    </div>
  );
}
